import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  DataSourcesType,
  OutputFormatting,
} from '../../SitrepsRoute/SitrepSectionWizard/types';

export const keys = {
  base: ['sectionTemplates'] as const,
  allByGroup: (group_guid: string) => [...keys.base, group_guid] as const,
};

export type SitrepSectionTemplateType = {
  id: UUID;
  name: string;
  type: 'manual' | 'ai';
  data_sources: DataSourcesType[];
  ai_instructions: string | null;
  output_format: string | null;
  output_format_structured: {
    summary_options: OutputFormatting[];
  };
  group_guid: UUID;
  archived: boolean;
  created_at: string;
  updated_at: string;
};
const fetchSectionTemplatesByGroup = async (group_guid: string) => {
  const response = await axios.get<SitrepSectionTemplateType>(
    `${config.apiGateway.sitrepSectionTemplates}/`,
    {
      params: { group_guid },
    }
  );
  return response.data;
};

// Fetch section templates
export const useSectionTemplates = () => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: keys.allByGroup(group_guid),
    queryFn: () => fetchSectionTemplatesByGroup(group_guid),
  });
};

export type CreateSectionTemplateArgs = {
  name: string;
  type: 'manual' | 'ai';
  data_sources?: DataSourcesType[];
  ai_instructions?: string;
  output_format?: string;
  output_format_structured: {
    summary_options: OutputFormatting[];
  };
};
export type CreateSectionTemplatePostArgs = CreateSectionTemplateArgs & {
  group_guid: string;
};

export const createSectionTemplate = async (
  vars: CreateSectionTemplateArgs,
  group_guid: UUID
) => {
  const response = await axios.post<
    unknown,
    AxiosResponse<{ id: string }>,
    CreateSectionTemplatePostArgs
  >(`${config.apiGateway.sitrepSectionTemplates}/`, {
    ...vars,
    group_guid,
  });
  return response.data;
};
// Create a section template
export const useCreateSectionTemplate = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);

  return useMutation<unknown, unknown, CreateSectionTemplateArgs>({
    mutationFn: (vars) => createSectionTemplate(vars, group_guid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.base });
    },
    onError: () => {
      toast.error('Failed to create section template.', toastConfig);
    },
  });
};

// Update a section template
export const useUpdateSectionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.put(
        `${config.apiGateway.sitrepSectionTemplates}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.base });
    },
    onError: () => {
      toast.error('Failed to update section template.', toastConfig);
    },
  });
};

// Delete a section template
export const useDeleteSectionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(
        `${config.apiGateway.sitrepSectionTemplates}/`,
        {
          data: { id },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.base });
    },
    onError: () => {
      toast.error('Failed to delete section template.', toastConfig);
    },
  });
};
